import type { Language } from "../../types/i18n";

/*
 * UI strings for one area of the app, merged into `translations` (see
 * ./index.ts) so components read them through `useI18nStore().t` like every
 * other key. English is the reference; the other languages must carry the
 * same keys, which the record type enforces.
 *
 * Covers allergies (alg*), medications (medMgmt*), the Apple Health / Google
 * Fit import box (ecoImport*) and lab results (lab*).
 */
const en = {
  // Allergies
  algAllergenLabel: "Allergen / substance",
  algCatFood: "Food",
  algCatMedication: "Medication",
  algCatEnvironment: "Environment",
  algCatBiologic: "Biologic",
  algCriticality: "Criticality",
  algCritLow: "Low",
  algCritHigh: "High",
  algCritUnknown: "Unknown",
  algCritLowBadge: "Low criticality",
  algCritHighBadge: "High criticality",
  algCritUnknownBadge: "Unknown criticality",
  algStatusResolved: "Resolved",
  algOnsetLabel: "First occurred on (optional)",
  algReactions: "Reactions",
  algAddReaction: "+ Add reaction",
  algSymptoms: "Symptoms",
  algSymptomsPlaceholder: "e.g. rash, shortness of breath",
  algSeverity: "Severity",
  algSevMild: "Mild",
  algSevModerate: "Moderate",
  algSevSevere: "Severe",
  algRemoveReaction: "Remove reaction",
  algReactionNotes: "Notes (optional)",
  algReactionNotesPlaceholder: "Additional information about the reaction",
  algRemoveAllergy: "Remove allergy",
  algAddNew: "Add new allergy",
  algSince: "Since:",
  algKnownReactions: "Known reactions:",
  algAllergenParacetamol: "Acetaminophen (paracetamol)",
  algAllergenPenicillin: "Penicillin",
  algAllergenAspirin: "Aspirin",
  algAllergenAmoxicillin: "Amoxicillin",
  algAllergenLatex: "Latex",
  algAllergenPollen: "Pollen",
  algAllergenNuts: "Nuts",
  algAllergenEggs: "Eggs",
  algAllergenCowsMilk: "Cow's milk",
  algAllergenSeafood: "Seafood",
  algAllergenChocolate: "Chocolate",
  algAllergenPeanuts: "Peanuts",
  algAllergenBeeStings: "Bee stings",
  algAllergenWaspStings: "Wasp stings",
  algAllergenAnesthetics: "Anesthetics",
  algAllergenIbuprofen: "Ibuprofen",

  // Medications
  medMgmtTitle: "Current Medications",
  medMgmtMedication: "Medication",
  medMgmtNamePlaceholder: "Enter medication name",
  medMgmtDosageForm: "Dosage form",
  medMgmtFormTablet: "Tablet",
  medMgmtFormCapsule: "Capsule",
  medMgmtFormLiquid: "Liquid",
  medMgmtFormInjection: "Injection",
  medMgmtFormCream: "Cream",
  medMgmtFormDrops: "Drops",
  medMgmtFormInhaler: "Inhaler",
  medMgmtFormPatch: "Patch",
  medMgmtStatusStopped: "Discontinued",
  medMgmtDosage: "Dosage",
  medMgmtFrequency: "Frequency",
  medMgmtFrequencyPlaceholder: "e.g. 2",
  medMgmtPeriod: "Period",
  medMgmtPeriodDaily: "Daily",
  medMgmtPeriodWeekly: "Weekly",
  medMgmtPeriodMonthly: "Monthly",
  medMgmtPeriodAsNeeded: "As needed",
  medMgmtTimesDaily: "{n}x daily",
  medMgmtTimesWeekly: "{n}x weekly",
  medMgmtTimesMonthly: "{n}x monthly",
  medMgmtTimesAsNeeded: "Up to {n}x as needed",
  medMgmtStrength: "Strength (optional)",
  medMgmtStrengthPlaceholder: "e.g. 500 mg",
  medMgmtInstructions: "Instructions (optional)",
  medMgmtInstructionsPlaceholder: "e.g. Take after meals",
  medMgmtPrescriber: "Prescribing doctor (optional)",
  medMgmtPrescriberPlaceholder: "Dr. Smith",
  medMgmtPrescribedBy: "Prescribed by: {name}",
  medMgmtRemove: "Remove medication",
  medMgmtAddNew: "Add new medication",
  medMgmtEmpty: "No medications documented",
  medMgmtCatAntibiotic: "Antibiotic",
  medMgmtCatMalaria: "Malaria prophylaxis",
  medMgmtCatAntifungal: "Antifungal",
  medMgmtCatPainReliever: "Pain reliever",
  medMgmtCatStrongPainReliever: "Strong pain reliever",
  medMgmtCatAntihistamine: "Antihistamine",
  medMgmtCatStomach: "Stomach protection",
  medMgmtCatBloodPressure: "Blood pressure medication",
  medMgmtCatBetaBlocker: "Beta blocker",
  medMgmtCatDiabetes: "Diabetes medication",
  medMgmtCatAntidepressant: "Antidepressant",
  medMgmtCatHormone: "Hormone therapy",
  medMgmtCatBloodThinner: "Blood thinner",
  medMgmtDrugAmoxiClav: "Amoxicillin/clavulanic acid",
  medMgmtDrugClarithromycin: "Clarithromycin",
  medMgmtDrugDoxycycline: "Doxycycline",
  medMgmtDrugChloroquine: "Chloroquine",
  medMgmtDrugAtovaquoneProguanil: "Atovaquone/proguanil",
  medMgmtDrugNystatin: "Nystatin",
  medMgmtDrugAspirin: "Aspirin (acetylsalicylic acid)",
  medMgmtDrugParacetamol: "Acetaminophen (paracetamol)",
  medMgmtDrugOxycodone: "Oxycodone",
  medMgmtDrugCetirizine: "Cetirizine",
  medMgmtDrugOmeprazole: "Omeprazole",
  medMgmtDrugEnalapril: "Enalapril",
  medMgmtDrugBisoprolol: "Bisoprolol",
  medMgmtDrugMetformin: "Metformin",
  medMgmtDrugSertraline: "Sertraline",
  medMgmtDrugFinasteride: "Finasteride",
  medMgmtDrugAspirinLowDose: "Low-dose aspirin",

  // Apple Health / Google Fit import
  ecoImportTitle: "Import from Apple Health or Google Fit",
  ecoImportSubtitle:
    "Bring existing data from your phone or smartwatch into HEALTHPASS.",
  ecoImportNoAppRequired: "No native app required",
  ecoImportAppleSubtitle: "Upload your extracted export.xml file",
  ecoImportAppleButton: "Import Apple Health XML",
  ecoImportAppleHint:
    "Export from the iPhone Health app, unzip the file, then upload the contained export.xml.",
  ecoImportAppleWrongFile:
    "Please upload the extracted Apple Health export.xml file.",
  ecoImportAppleSuccess: "Apple Health data imported successfully.",
  ecoImportAppleFailed: "Apple Health import failed.",
  ecoImportGoogleConnected:
    "Google Fit connected successfully. You can sync your data now.",
  ecoImportGoogleConnectFailed:
    "Google Fit connection failed. Please try again.",
  ecoImportGoogleNotConfigured: "Google Fit is not configured yet.",
  ecoImportGoogleSyncSuccess: "Google Fit data synced successfully.",
  ecoImportGoogleSyncFailed: "Google Fit sync failed.",
  ecoImportGoogleDisconnected: "Google Fit disconnected.",
  ecoImportGoogleDisconnectFailed: "Failed to disconnect Google Fit.",
  ecoImportGoogleStatusConnected: "Connected · last sync {time}",
  ecoImportGoogleConnectPrompt: "Connect your Google account",
  ecoImportGoogleConnect: "Connect Google Fit",
  ecoImportSyncNow: "Sync now",
  ecoImportGoogleDisconnect: "Disconnect Google Fit",
  ecoImportSummary: "Import summary",
  ecoImportStatTotal: "Total",
  ecoImportStatSteps: "Steps",

  // Lab results
  labValuePlaceholder: "e.g. 120",
  labUnitPlaceholder: "e.g. mg/dL",
  labPerformerPlaceholder: "Dr. Smith / ABC Lab",
  labRemove: "Remove lab result",
  labPerformedBy: "by {name}",
  labChartComingSoon: "{name} chart coming soon",
  labChartNoData: "No data available to display",
  labChartFilter: "Filter: {name}",
  labChartDefaultName: "Lab value",
  labChartRefOptimal: "Optimal: {value}",
  labChartRefNormal: "Normal: {value}",
  labChartRefHigh: "High: {value}",
  labTestLdl: "LDL cholesterol",
  labTestTotalCholesterol: "Total cholesterol",
  labTestHdl: "HDL cholesterol",
  labTestTriglycerides: "Triglycerides",
  labTestHba1c: "Hemoglobin A1c",
  labTestHemoglobin: "Hemoglobin",
  labTestWbc: "White blood cell count",
  labTestPlatelets: "Platelet count",
};

export type HealthAllergiesMedsStrings = typeof en;

const de: HealthAllergiesMedsStrings = {
  algAllergenLabel: "Allergen / Substanz",
  algCatFood: "Lebensmittel",
  algCatMedication: "Medikament",
  algCatEnvironment: "Umwelt",
  algCatBiologic: "Biologisch",
  algCriticality: "Kritikalität",
  algCritLow: "Niedrig",
  algCritHigh: "Hoch",
  algCritUnknown: "Unbekannt",
  algCritLowBadge: "Geringe Kritikalität",
  algCritHighBadge: "Hohe Kritikalität",
  algCritUnknownBadge: "Kritikalität unbekannt",
  algStatusResolved: "Abgeklungen",
  algOnsetLabel: "Erstmals aufgetreten am (optional)",
  algReactions: "Reaktionen",
  algAddReaction: "+ Reaktion hinzufügen",
  algSymptoms: "Symptome",
  algSymptomsPlaceholder: "z. B. Hautausschlag, Atemnot",
  algSeverity: "Schweregrad",
  algSevMild: "Leicht",
  algSevModerate: "Mittel",
  algSevSevere: "Schwer",
  algRemoveReaction: "Reaktion entfernen",
  algReactionNotes: "Notizen (optional)",
  algReactionNotesPlaceholder: "Weitere Angaben zur Reaktion",
  algRemoveAllergy: "Allergie entfernen",
  algAddNew: "Neue Allergie hinzufügen",
  algSince: "Seit:",
  algKnownReactions: "Bekannte Reaktionen:",
  algAllergenParacetamol: "Paracetamol",
  algAllergenPenicillin: "Penicillin",
  algAllergenAspirin: "Acetylsalicylsäure (Aspirin)",
  algAllergenAmoxicillin: "Amoxicillin",
  algAllergenLatex: "Latex",
  algAllergenPollen: "Pollen",
  algAllergenNuts: "Nüsse",
  algAllergenEggs: "Eier",
  algAllergenCowsMilk: "Kuhmilch",
  algAllergenSeafood: "Meeresfrüchte",
  algAllergenChocolate: "Schokolade",
  algAllergenPeanuts: "Erdnüsse",
  algAllergenBeeStings: "Bienenstiche",
  algAllergenWaspStings: "Wespenstiche",
  algAllergenAnesthetics: "Anästhetika",
  algAllergenIbuprofen: "Ibuprofen",

  medMgmtTitle: "Aktuelle Medikation",
  medMgmtMedication: "Medikament",
  medMgmtNamePlaceholder: "Name des Medikaments eingeben",
  medMgmtDosageForm: "Darreichungsform",
  medMgmtFormTablet: "Tablette",
  medMgmtFormCapsule: "Kapsel",
  medMgmtFormLiquid: "Flüssigkeit",
  medMgmtFormInjection: "Injektion",
  medMgmtFormCream: "Creme",
  medMgmtFormDrops: "Tropfen",
  medMgmtFormInhaler: "Inhalator",
  medMgmtFormPatch: "Pflaster",
  medMgmtStatusStopped: "Abgesetzt",
  medMgmtDosage: "Dosierung",
  medMgmtFrequency: "Häufigkeit",
  medMgmtFrequencyPlaceholder: "z. B. 2",
  medMgmtPeriod: "Zeitraum",
  medMgmtPeriodDaily: "Täglich",
  medMgmtPeriodWeekly: "Wöchentlich",
  medMgmtPeriodMonthly: "Monatlich",
  medMgmtPeriodAsNeeded: "Bei Bedarf",
  medMgmtTimesDaily: "{n}x täglich",
  medMgmtTimesWeekly: "{n}x wöchentlich",
  medMgmtTimesMonthly: "{n}x monatlich",
  medMgmtTimesAsNeeded: "Bis zu {n}x bei Bedarf",
  medMgmtStrength: "Stärke (optional)",
  medMgmtStrengthPlaceholder: "z. B. 500 mg",
  medMgmtInstructions: "Einnahmehinweise (optional)",
  medMgmtInstructionsPlaceholder: "z. B. nach dem Essen einnehmen",
  medMgmtPrescriber: "Verschreibender Arzt (optional)",
  medMgmtPrescriberPlaceholder: "Dr. Mustermann",
  medMgmtPrescribedBy: "Verschrieben von: {name}",
  medMgmtRemove: "Medikament entfernen",
  medMgmtAddNew: "Neues Medikament hinzufügen",
  medMgmtEmpty: "Keine Medikamente dokumentiert",
  medMgmtCatAntibiotic: "Antibiotikum",
  medMgmtCatMalaria: "Malariaprophylaxe",
  medMgmtCatAntifungal: "Antimykotikum",
  medMgmtCatPainReliever: "Schmerzmittel",
  medMgmtCatStrongPainReliever: "Starkes Schmerzmittel",
  medMgmtCatAntihistamine: "Antihistaminikum",
  medMgmtCatStomach: "Magenschutz",
  medMgmtCatBloodPressure: "Blutdrucksenker",
  medMgmtCatBetaBlocker: "Betablocker",
  medMgmtCatDiabetes: "Diabetes-Medikament",
  medMgmtCatAntidepressant: "Antidepressivum",
  medMgmtCatHormone: "Hormontherapie",
  medMgmtCatBloodThinner: "Blutverdünner",
  medMgmtDrugAmoxiClav: "Amoxicillin/Clavulansäure",
  medMgmtDrugClarithromycin: "Clarithromycin",
  medMgmtDrugDoxycycline: "Doxycyclin",
  medMgmtDrugChloroquine: "Chloroquin",
  medMgmtDrugAtovaquoneProguanil: "Atovaquon/Proguanil",
  medMgmtDrugNystatin: "Nystatin",
  medMgmtDrugAspirin: "Acetylsalicylsäure",
  medMgmtDrugParacetamol: "Paracetamol",
  medMgmtDrugOxycodone: "Oxycodon",
  medMgmtDrugCetirizine: "Cetirizin",
  medMgmtDrugOmeprazole: "Omeprazol",
  medMgmtDrugEnalapril: "Enalapril",
  medMgmtDrugBisoprolol: "Bisoprolol",
  medMgmtDrugMetformin: "Metformin",
  medMgmtDrugSertraline: "Sertralin",
  medMgmtDrugFinasteride: "Finasterid",
  medMgmtDrugAspirinLowDose: "Acetylsalicylsäure (niedrig dosiert)",

  ecoImportTitle: "Import aus Apple Health oder Google Fit",
  ecoImportSubtitle:
    "Übernehmen Sie vorhandene Daten von Ihrem Smartphone oder Ihrer Smartwatch in HEALTHPASS.",
  ecoImportNoAppRequired: "Keine native App nötig",
  ecoImportAppleSubtitle: "Laden Sie Ihre entpackte export.xml-Datei hoch",
  ecoImportAppleButton: "Apple-Health-XML importieren",
  ecoImportAppleHint:
    "Exportieren Sie die Daten in der Health-App des iPhones, entpacken Sie die Datei und laden Sie die enthaltene export.xml hoch.",
  ecoImportAppleWrongFile:
    "Bitte laden Sie die entpackte Apple-Health-Datei export.xml hoch.",
  ecoImportAppleSuccess: "Apple-Health-Daten erfolgreich importiert.",
  ecoImportAppleFailed: "Import aus Apple Health fehlgeschlagen.",
  ecoImportGoogleConnected:
    "Google Fit erfolgreich verbunden. Sie können Ihre Daten jetzt synchronisieren.",
  ecoImportGoogleConnectFailed:
    "Verbindung mit Google Fit fehlgeschlagen. Bitte versuchen Sie es erneut.",
  ecoImportGoogleNotConfigured: "Google Fit ist noch nicht eingerichtet.",
  ecoImportGoogleSyncSuccess: "Google-Fit-Daten erfolgreich synchronisiert.",
  ecoImportGoogleSyncFailed: "Synchronisierung mit Google Fit fehlgeschlagen.",
  ecoImportGoogleDisconnected: "Google Fit getrennt.",
  ecoImportGoogleDisconnectFailed: "Google Fit konnte nicht getrennt werden.",
  ecoImportGoogleStatusConnected: "Verbunden · letzte Synchronisierung {time}",
  ecoImportGoogleConnectPrompt: "Verbinden Sie Ihr Google-Konto",
  ecoImportGoogleConnect: "Google Fit verbinden",
  ecoImportSyncNow: "Jetzt synchronisieren",
  ecoImportGoogleDisconnect: "Google Fit trennen",
  ecoImportSummary: "Importübersicht",
  ecoImportStatTotal: "Gesamt",
  ecoImportStatSteps: "Schritte",

  labValuePlaceholder: "z. B. 120",
  labUnitPlaceholder: "z. B. mg/dL",
  labPerformerPlaceholder: "Dr. Mustermann / Labor ABC",
  labRemove: "Laborwert entfernen",
  labPerformedBy: "von {name}",
  labChartComingSoon: "Diagramm für {name} folgt in Kürze",
  labChartNoData: "Keine Daten für die Anzeige verfügbar",
  labChartFilter: "Filter: {name}",
  labChartDefaultName: "Laborwert",
  labChartRefOptimal: "Optimal: {value}",
  labChartRefNormal: "Normal: {value}",
  labChartRefHigh: "Hoch: {value}",
  labTestLdl: "LDL-Cholesterin",
  labTestTotalCholesterol: "Gesamtcholesterin",
  labTestHdl: "HDL-Cholesterin",
  labTestTriglycerides: "Triglyceride",
  labTestHba1c: "HbA1c",
  labTestHemoglobin: "Hämoglobin",
  labTestWbc: "Leukozyten",
  labTestPlatelets: "Thrombozyten",
};

const fr: HealthAllergiesMedsStrings = {
  algAllergenLabel: "Allergène / substance",
  algCatFood: "Aliment",
  algCatMedication: "Médicament",
  algCatEnvironment: "Environnement",
  algCatBiologic: "Biologique",
  algCriticality: "Criticité",
  algCritLow: "Faible",
  algCritHigh: "Élevée",
  algCritUnknown: "Inconnue",
  algCritLowBadge: "Criticité faible",
  algCritHighBadge: "Criticité élevée",
  algCritUnknownBadge: "Criticité inconnue",
  algStatusResolved: "Résolue",
  algOnsetLabel: "Première apparition le (facultatif)",
  algReactions: "Réactions",
  algAddReaction: "+ Ajouter une réaction",
  algSymptoms: "Symptômes",
  algSymptomsPlaceholder: "p. ex. éruption cutanée, essoufflement",
  algSeverity: "Gravité",
  algSevMild: "Légère",
  algSevModerate: "Modérée",
  algSevSevere: "Sévère",
  algRemoveReaction: "Supprimer la réaction",
  algReactionNotes: "Remarques (facultatif)",
  algReactionNotesPlaceholder: "Informations complémentaires sur la réaction",
  algRemoveAllergy: "Supprimer l'allergie",
  algAddNew: "Ajouter une allergie",
  algSince: "Depuis :",
  algKnownReactions: "Réactions connues :",
  algAllergenParacetamol: "Paracétamol",
  algAllergenPenicillin: "Pénicilline",
  algAllergenAspirin: "Aspirine",
  algAllergenAmoxicillin: "Amoxicilline",
  algAllergenLatex: "Latex",
  algAllergenPollen: "Pollen",
  algAllergenNuts: "Fruits à coque",
  algAllergenEggs: "Œufs",
  algAllergenCowsMilk: "Lait de vache",
  algAllergenSeafood: "Fruits de mer",
  algAllergenChocolate: "Chocolat",
  algAllergenPeanuts: "Arachides",
  algAllergenBeeStings: "Piqûres d'abeille",
  algAllergenWaspStings: "Piqûres de guêpe",
  algAllergenAnesthetics: "Anesthésiques",
  algAllergenIbuprofen: "Ibuprofène",

  medMgmtTitle: "Traitement en cours",
  medMgmtMedication: "Médicament",
  medMgmtNamePlaceholder: "Saisir le nom du médicament",
  medMgmtDosageForm: "Forme galénique",
  medMgmtFormTablet: "Comprimé",
  medMgmtFormCapsule: "Gélule",
  medMgmtFormLiquid: "Liquide",
  medMgmtFormInjection: "Injection",
  medMgmtFormCream: "Crème",
  medMgmtFormDrops: "Gouttes",
  medMgmtFormInhaler: "Inhalateur",
  medMgmtFormPatch: "Patch",
  medMgmtStatusStopped: "Arrêté",
  medMgmtDosage: "Posologie",
  medMgmtFrequency: "Fréquence",
  medMgmtFrequencyPlaceholder: "p. ex. 2",
  medMgmtPeriod: "Période",
  medMgmtPeriodDaily: "Quotidien",
  medMgmtPeriodWeekly: "Hebdomadaire",
  medMgmtPeriodMonthly: "Mensuel",
  medMgmtPeriodAsNeeded: "Si besoin",
  medMgmtTimesDaily: "{n} fois par jour",
  medMgmtTimesWeekly: "{n} fois par semaine",
  medMgmtTimesMonthly: "{n} fois par mois",
  medMgmtTimesAsNeeded: "Jusqu'à {n} fois si besoin",
  medMgmtStrength: "Dosage (facultatif)",
  medMgmtStrengthPlaceholder: "p. ex. 500 mg",
  medMgmtInstructions: "Consignes de prise (facultatif)",
  medMgmtInstructionsPlaceholder: "p. ex. à prendre après le repas",
  medMgmtPrescriber: "Médecin prescripteur (facultatif)",
  medMgmtPrescriberPlaceholder: "Dr Martin",
  medMgmtPrescribedBy: "Prescrit par : {name}",
  medMgmtRemove: "Supprimer le médicament",
  medMgmtAddNew: "Ajouter un médicament",
  medMgmtEmpty: "Aucun médicament documenté",
  medMgmtCatAntibiotic: "Antibiotique",
  medMgmtCatMalaria: "Prophylaxie antipaludique",
  medMgmtCatAntifungal: "Antifongique",
  medMgmtCatPainReliever: "Antalgique",
  medMgmtCatStrongPainReliever: "Antalgique puissant",
  medMgmtCatAntihistamine: "Antihistaminique",
  medMgmtCatStomach: "Protecteur gastrique",
  medMgmtCatBloodPressure: "Antihypertenseur",
  medMgmtCatBetaBlocker: "Bêtabloquant",
  medMgmtCatDiabetes: "Antidiabétique",
  medMgmtCatAntidepressant: "Antidépresseur",
  medMgmtCatHormone: "Hormonothérapie",
  medMgmtCatBloodThinner: "Anticoagulant / antiagrégant",
  medMgmtDrugAmoxiClav: "Amoxicilline/acide clavulanique",
  medMgmtDrugClarithromycin: "Clarithromycine",
  medMgmtDrugDoxycycline: "Doxycycline",
  medMgmtDrugChloroquine: "Chloroquine",
  medMgmtDrugAtovaquoneProguanil: "Atovaquone/proguanil",
  medMgmtDrugNystatin: "Nystatine",
  medMgmtDrugAspirin: "Acide acétylsalicylique",
  medMgmtDrugParacetamol: "Paracétamol",
  medMgmtDrugOxycodone: "Oxycodone",
  medMgmtDrugCetirizine: "Cétirizine",
  medMgmtDrugOmeprazole: "Oméprazole",
  medMgmtDrugEnalapril: "Énalapril",
  medMgmtDrugBisoprolol: "Bisoprolol",
  medMgmtDrugMetformin: "Metformine",
  medMgmtDrugSertraline: "Sertraline",
  medMgmtDrugFinasteride: "Finastéride",
  medMgmtDrugAspirinLowDose: "Acide acétylsalicylique (faible dose)",

  ecoImportTitle: "Importer depuis Apple Santé ou Google Fit",
  ecoImportSubtitle:
    "Transférez dans HEALTHPASS les données déjà présentes sur votre téléphone ou votre montre connectée.",
  ecoImportNoAppRequired: "Aucune application native requise",
  ecoImportAppleSubtitle: "Téléversez votre fichier export.xml décompressé",
  ecoImportAppleButton: "Importer le XML Apple Santé",
  ecoImportAppleHint:
    "Exportez depuis l'app Santé de l'iPhone, décompressez le fichier, puis téléversez le fichier export.xml qu'il contient.",
  ecoImportAppleWrongFile:
    "Veuillez téléverser le fichier export.xml décompressé d'Apple Santé.",
  ecoImportAppleSuccess: "Données Apple Santé importées avec succès.",
  ecoImportAppleFailed: "L'import depuis Apple Santé a échoué.",
  ecoImportGoogleConnected:
    "Google Fit connecté avec succès. Vous pouvez maintenant synchroniser vos données.",
  ecoImportGoogleConnectFailed:
    "La connexion à Google Fit a échoué. Veuillez réessayer.",
  ecoImportGoogleNotConfigured: "Google Fit n'est pas encore configuré.",
  ecoImportGoogleSyncSuccess: "Données Google Fit synchronisées avec succès.",
  ecoImportGoogleSyncFailed: "La synchronisation Google Fit a échoué.",
  ecoImportGoogleDisconnected: "Google Fit déconnecté.",
  ecoImportGoogleDisconnectFailed: "Impossible de déconnecter Google Fit.",
  ecoImportGoogleStatusConnected: "Connecté · dernière synchronisation {time}",
  ecoImportGoogleConnectPrompt: "Connectez votre compte Google",
  ecoImportGoogleConnect: "Connecter Google Fit",
  ecoImportSyncNow: "Synchroniser",
  ecoImportGoogleDisconnect: "Déconnecter Google Fit",
  ecoImportSummary: "Récapitulatif de l'import",
  ecoImportStatTotal: "Total",
  ecoImportStatSteps: "Pas",

  labValuePlaceholder: "p. ex. 120",
  labUnitPlaceholder: "p. ex. mg/dL",
  labPerformerPlaceholder: "Dr Martin / Laboratoire ABC",
  labRemove: "Supprimer le résultat",
  labPerformedBy: "par {name}",
  labChartComingSoon: "Graphique {name} bientôt disponible",
  labChartNoData: "Aucune donnée à afficher",
  labChartFilter: "Filtre : {name}",
  labChartDefaultName: "Valeur de laboratoire",
  labChartRefOptimal: "Optimal : {value}",
  labChartRefNormal: "Normal : {value}",
  labChartRefHigh: "Élevé : {value}",
  labTestLdl: "Cholestérol LDL",
  labTestTotalCholesterol: "Cholestérol total",
  labTestHdl: "Cholestérol HDL",
  labTestTriglycerides: "Triglycérides",
  labTestHba1c: "Hémoglobine glyquée (HbA1c)",
  labTestHemoglobin: "Hémoglobine",
  labTestWbc: "Leucocytes",
  labTestPlatelets: "Plaquettes",
};

const es: HealthAllergiesMedsStrings = {
  algAllergenLabel: "Alérgeno / sustancia",
  algCatFood: "Alimento",
  algCatMedication: "Medicamento",
  algCatEnvironment: "Ambiental",
  algCatBiologic: "Biológico",
  algCriticality: "Criticidad",
  algCritLow: "Baja",
  algCritHigh: "Alta",
  algCritUnknown: "Desconocida",
  algCritLowBadge: "Criticidad baja",
  algCritHighBadge: "Criticidad alta",
  algCritUnknownBadge: "Criticidad desconocida",
  algStatusResolved: "Resuelta",
  algOnsetLabel: "Primera aparición (opcional)",
  algReactions: "Reacciones",
  algAddReaction: "+ Añadir reacción",
  algSymptoms: "Síntomas",
  algSymptomsPlaceholder: "p. ej., erupción cutánea, dificultad para respirar",
  algSeverity: "Gravedad",
  algSevMild: "Leve",
  algSevModerate: "Moderada",
  algSevSevere: "Grave",
  algRemoveReaction: "Eliminar reacción",
  algReactionNotes: "Notas (opcional)",
  algReactionNotesPlaceholder: "Información adicional sobre la reacción",
  algRemoveAllergy: "Eliminar alergia",
  algAddNew: "Añadir nueva alergia",
  algSince: "Desde:",
  algKnownReactions: "Reacciones conocidas:",
  algAllergenParacetamol: "Paracetamol",
  algAllergenPenicillin: "Penicilina",
  algAllergenAspirin: "Aspirina",
  algAllergenAmoxicillin: "Amoxicilina",
  algAllergenLatex: "Látex",
  algAllergenPollen: "Polen",
  algAllergenNuts: "Frutos secos",
  algAllergenEggs: "Huevos",
  algAllergenCowsMilk: "Leche de vaca",
  algAllergenSeafood: "Mariscos",
  algAllergenChocolate: "Chocolate",
  algAllergenPeanuts: "Cacahuetes",
  algAllergenBeeStings: "Picaduras de abeja",
  algAllergenWaspStings: "Picaduras de avispa",
  algAllergenAnesthetics: "Anestésicos",
  algAllergenIbuprofen: "Ibuprofeno",

  medMgmtTitle: "Medicación actual",
  medMgmtMedication: "Medicamento",
  medMgmtNamePlaceholder: "Introduzca el nombre del medicamento",
  medMgmtDosageForm: "Forma farmacéutica",
  medMgmtFormTablet: "Comprimido",
  medMgmtFormCapsule: "Cápsula",
  medMgmtFormLiquid: "Líquido",
  medMgmtFormInjection: "Inyección",
  medMgmtFormCream: "Crema",
  medMgmtFormDrops: "Gotas",
  medMgmtFormInhaler: "Inhalador",
  medMgmtFormPatch: "Parche",
  medMgmtStatusStopped: "Suspendido",
  medMgmtDosage: "Posología",
  medMgmtFrequency: "Frecuencia",
  medMgmtFrequencyPlaceholder: "p. ej., 2",
  medMgmtPeriod: "Periodo",
  medMgmtPeriodDaily: "Diario",
  medMgmtPeriodWeekly: "Semanal",
  medMgmtPeriodMonthly: "Mensual",
  medMgmtPeriodAsNeeded: "Según necesidad",
  medMgmtTimesDaily: "{n} veces al día",
  medMgmtTimesWeekly: "{n} veces por semana",
  medMgmtTimesMonthly: "{n} veces al mes",
  medMgmtTimesAsNeeded: "Hasta {n} veces según necesidad",
  medMgmtStrength: "Dosis (opcional)",
  medMgmtStrengthPlaceholder: "p. ej., 500 mg",
  medMgmtInstructions: "Instrucciones de toma (opcional)",
  medMgmtInstructionsPlaceholder: "p. ej., tomar después de las comidas",
  medMgmtPrescriber: "Médico prescriptor (opcional)",
  medMgmtPrescriberPlaceholder: "Dr. García",
  medMgmtPrescribedBy: "Recetado por: {name}",
  medMgmtRemove: "Eliminar medicamento",
  medMgmtAddNew: "Añadir nuevo medicamento",
  medMgmtEmpty: "No hay medicamentos registrados",
  medMgmtCatAntibiotic: "Antibiótico",
  medMgmtCatMalaria: "Profilaxis antipalúdica",
  medMgmtCatAntifungal: "Antifúngico",
  medMgmtCatPainReliever: "Analgésico",
  medMgmtCatStrongPainReliever: "Analgésico potente",
  medMgmtCatAntihistamine: "Antihistamínico",
  medMgmtCatStomach: "Protector gástrico",
  medMgmtCatBloodPressure: "Antihipertensivo",
  medMgmtCatBetaBlocker: "Betabloqueante",
  medMgmtCatDiabetes: "Antidiabético",
  medMgmtCatAntidepressant: "Antidepresivo",
  medMgmtCatHormone: "Terapia hormonal",
  medMgmtCatBloodThinner: "Anticoagulante / antiagregante",
  medMgmtDrugAmoxiClav: "Amoxicilina/ácido clavulánico",
  medMgmtDrugClarithromycin: "Claritromicina",
  medMgmtDrugDoxycycline: "Doxiciclina",
  medMgmtDrugChloroquine: "Cloroquina",
  medMgmtDrugAtovaquoneProguanil: "Atovacuona/proguanil",
  medMgmtDrugNystatin: "Nistatina",
  medMgmtDrugAspirin: "Ácido acetilsalicílico",
  medMgmtDrugParacetamol: "Paracetamol",
  medMgmtDrugOxycodone: "Oxicodona",
  medMgmtDrugCetirizine: "Cetirizina",
  medMgmtDrugOmeprazole: "Omeprazol",
  medMgmtDrugEnalapril: "Enalapril",
  medMgmtDrugBisoprolol: "Bisoprolol",
  medMgmtDrugMetformin: "Metformina",
  medMgmtDrugSertraline: "Sertralina",
  medMgmtDrugFinasteride: "Finasterida",
  medMgmtDrugAspirinLowDose: "Ácido acetilsalicílico (dosis baja)",

  ecoImportTitle: "Importar desde Apple Salud o Google Fit",
  ecoImportSubtitle:
    "Traiga a HEALTHPASS los datos que ya tiene en su teléfono o reloj inteligente.",
  ecoImportNoAppRequired: "No requiere app nativa",
  ecoImportAppleSubtitle: "Suba su archivo export.xml descomprimido",
  ecoImportAppleButton: "Importar XML de Apple Salud",
  ecoImportAppleHint:
    "Exporte desde la app Salud del iPhone, descomprima el archivo y suba el export.xml que contiene.",
  ecoImportAppleWrongFile:
    "Suba el archivo export.xml descomprimido de Apple Salud.",
  ecoImportAppleSuccess: "Datos de Apple Salud importados correctamente.",
  ecoImportAppleFailed: "La importación desde Apple Salud ha fallado.",
  ecoImportGoogleConnected:
    "Google Fit se ha conectado correctamente. Ya puede sincronizar sus datos.",
  ecoImportGoogleConnectFailed:
    "No se pudo conectar con Google Fit. Inténtelo de nuevo.",
  ecoImportGoogleNotConfigured: "Google Fit aún no está configurado.",
  ecoImportGoogleSyncSuccess:
    "Datos de Google Fit sincronizados correctamente.",
  ecoImportGoogleSyncFailed: "La sincronización con Google Fit ha fallado.",
  ecoImportGoogleDisconnected: "Google Fit desconectado.",
  ecoImportGoogleDisconnectFailed: "No se pudo desconectar Google Fit.",
  ecoImportGoogleStatusConnected: "Conectado · última sincronización {time}",
  ecoImportGoogleConnectPrompt: "Conecte su cuenta de Google",
  ecoImportGoogleConnect: "Conectar Google Fit",
  ecoImportSyncNow: "Sincronizar ahora",
  ecoImportGoogleDisconnect: "Desconectar Google Fit",
  ecoImportSummary: "Resumen de la importación",
  ecoImportStatTotal: "Total",
  ecoImportStatSteps: "Pasos",

  labValuePlaceholder: "p. ej., 120",
  labUnitPlaceholder: "p. ej., mg/dL",
  labPerformerPlaceholder: "Dr. García / Laboratorio ABC",
  labRemove: "Eliminar resultado",
  labPerformedBy: "por {name}",
  labChartComingSoon: "Gráfico de {name} próximamente",
  labChartNoData: "No hay datos para mostrar",
  labChartFilter: "Filtro: {name}",
  labChartDefaultName: "Valor de laboratorio",
  labChartRefOptimal: "Óptimo: {value}",
  labChartRefNormal: "Normal: {value}",
  labChartRefHigh: "Alto: {value}",
  labTestLdl: "Colesterol LDL",
  labTestTotalCholesterol: "Colesterol total",
  labTestHdl: "Colesterol HDL",
  labTestTriglycerides: "Triglicéridos",
  labTestHba1c: "Hemoglobina glucosilada (HbA1c)",
  labTestHemoglobin: "Hemoglobina",
  labTestWbc: "Leucocitos",
  labTestPlatelets: "Plaquetas",
};

const pt: HealthAllergiesMedsStrings = {
  algAllergenLabel: "Alergénio / substância",
  algCatFood: "Alimento",
  algCatMedication: "Medicamento",
  algCatEnvironment: "Ambiental",
  algCatBiologic: "Biológico",
  algCriticality: "Criticidade",
  algCritLow: "Baixa",
  algCritHigh: "Alta",
  algCritUnknown: "Desconhecida",
  algCritLowBadge: "Criticidade baixa",
  algCritHighBadge: "Criticidade alta",
  algCritUnknownBadge: "Criticidade desconhecida",
  algStatusResolved: "Resolvida",
  algOnsetLabel: "Primeira ocorrência em (opcional)",
  algReactions: "Reações",
  algAddReaction: "+ Adicionar reação",
  algSymptoms: "Sintomas",
  algSymptomsPlaceholder: "p. ex. erupção cutânea, falta de ar",
  algSeverity: "Gravidade",
  algSevMild: "Ligeira",
  algSevModerate: "Moderada",
  algSevSevere: "Grave",
  algRemoveReaction: "Remover reação",
  algReactionNotes: "Notas (opcional)",
  algReactionNotesPlaceholder: "Informações adicionais sobre a reação",
  algRemoveAllergy: "Remover alergia",
  algAddNew: "Adicionar nova alergia",
  algSince: "Desde:",
  algKnownReactions: "Reações conhecidas:",
  algAllergenParacetamol: "Paracetamol",
  algAllergenPenicillin: "Penicilina",
  algAllergenAspirin: "Aspirina",
  algAllergenAmoxicillin: "Amoxicilina",
  algAllergenLatex: "Látex",
  algAllergenPollen: "Pólen",
  algAllergenNuts: "Frutos de casca rija",
  algAllergenEggs: "Ovos",
  algAllergenCowsMilk: "Leite de vaca",
  algAllergenSeafood: "Marisco",
  algAllergenChocolate: "Chocolate",
  algAllergenPeanuts: "Amendoins",
  algAllergenBeeStings: "Picadas de abelha",
  algAllergenWaspStings: "Picadas de vespa",
  algAllergenAnesthetics: "Anestésicos",
  algAllergenIbuprofen: "Ibuprofeno",

  medMgmtTitle: "Medicação atual",
  medMgmtMedication: "Medicamento",
  medMgmtNamePlaceholder: "Introduza o nome do medicamento",
  medMgmtDosageForm: "Forma farmacêutica",
  medMgmtFormTablet: "Comprimido",
  medMgmtFormCapsule: "Cápsula",
  medMgmtFormLiquid: "Líquido",
  medMgmtFormInjection: "Injeção",
  medMgmtFormCream: "Creme",
  medMgmtFormDrops: "Gotas",
  medMgmtFormInhaler: "Inalador",
  medMgmtFormPatch: "Adesivo",
  medMgmtStatusStopped: "Suspenso",
  medMgmtDosage: "Posologia",
  medMgmtFrequency: "Frequência",
  medMgmtFrequencyPlaceholder: "p. ex. 2",
  medMgmtPeriod: "Período",
  medMgmtPeriodDaily: "Diário",
  medMgmtPeriodWeekly: "Semanal",
  medMgmtPeriodMonthly: "Mensal",
  medMgmtPeriodAsNeeded: "Se necessário",
  medMgmtTimesDaily: "{n}x por dia",
  medMgmtTimesWeekly: "{n}x por semana",
  medMgmtTimesMonthly: "{n}x por mês",
  medMgmtTimesAsNeeded: "Até {n}x se necessário",
  medMgmtStrength: "Dosagem (opcional)",
  medMgmtStrengthPlaceholder: "p. ex. 500 mg",
  medMgmtInstructions: "Instruções de toma (opcional)",
  medMgmtInstructionsPlaceholder: "p. ex. tomar depois das refeições",
  medMgmtPrescriber: "Médico prescritor (opcional)",
  medMgmtPrescriberPlaceholder: "Dr. Silva",
  medMgmtPrescribedBy: "Prescrito por: {name}",
  medMgmtRemove: "Remover medicamento",
  medMgmtAddNew: "Adicionar novo medicamento",
  medMgmtEmpty: "Nenhum medicamento registado",
  medMgmtCatAntibiotic: "Antibiótico",
  medMgmtCatMalaria: "Profilaxia da malária",
  medMgmtCatAntifungal: "Antifúngico",
  medMgmtCatPainReliever: "Analgésico",
  medMgmtCatStrongPainReliever: "Analgésico forte",
  medMgmtCatAntihistamine: "Anti-histamínico",
  medMgmtCatStomach: "Protetor gástrico",
  medMgmtCatBloodPressure: "Anti-hipertensor",
  medMgmtCatBetaBlocker: "Betabloqueador",
  medMgmtCatDiabetes: "Antidiabético",
  medMgmtCatAntidepressant: "Antidepressivo",
  medMgmtCatHormone: "Terapêutica hormonal",
  medMgmtCatBloodThinner: "Anticoagulante / antiagregante",
  medMgmtDrugAmoxiClav: "Amoxicilina/ácido clavulânico",
  medMgmtDrugClarithromycin: "Claritromicina",
  medMgmtDrugDoxycycline: "Doxiciclina",
  medMgmtDrugChloroquine: "Cloroquina",
  medMgmtDrugAtovaquoneProguanil: "Atovaquona/proguanil",
  medMgmtDrugNystatin: "Nistatina",
  medMgmtDrugAspirin: "Ácido acetilsalicílico",
  medMgmtDrugParacetamol: "Paracetamol",
  medMgmtDrugOxycodone: "Oxicodona",
  medMgmtDrugCetirizine: "Cetirizina",
  medMgmtDrugOmeprazole: "Omeprazol",
  medMgmtDrugEnalapril: "Enalapril",
  medMgmtDrugBisoprolol: "Bisoprolol",
  medMgmtDrugMetformin: "Metformina",
  medMgmtDrugSertraline: "Sertralina",
  medMgmtDrugFinasteride: "Finasterida",
  medMgmtDrugAspirinLowDose: "Ácido acetilsalicílico (dose baixa)",

  ecoImportTitle: "Importar do Apple Saúde ou do Google Fit",
  ecoImportSubtitle:
    "Traga para o HEALTHPASS os dados que já tem no telemóvel ou no smartwatch.",
  ecoImportNoAppRequired: "Não requer app nativa",
  ecoImportAppleSubtitle: "Carregue o ficheiro export.xml descompactado",
  ecoImportAppleButton: "Importar XML do Apple Saúde",
  ecoImportAppleHint:
    "Exporte a partir da app Saúde do iPhone, descompacte o ficheiro e carregue o export.xml que contém.",
  ecoImportAppleWrongFile:
    "Carregue o ficheiro export.xml descompactado do Apple Saúde.",
  ecoImportAppleSuccess: "Dados do Apple Saúde importados com sucesso.",
  ecoImportAppleFailed: "A importação do Apple Saúde falhou.",
  ecoImportGoogleConnected:
    "Google Fit ligado com sucesso. Já pode sincronizar os seus dados.",
  ecoImportGoogleConnectFailed:
    "A ligação ao Google Fit falhou. Tente novamente.",
  ecoImportGoogleNotConfigured: "O Google Fit ainda não está configurado.",
  ecoImportGoogleSyncSuccess: "Dados do Google Fit sincronizados com sucesso.",
  ecoImportGoogleSyncFailed: "A sincronização com o Google Fit falhou.",
  ecoImportGoogleDisconnected: "Google Fit desligado.",
  ecoImportGoogleDisconnectFailed: "Não foi possível desligar o Google Fit.",
  ecoImportGoogleStatusConnected: "Ligado · última sincronização {time}",
  ecoImportGoogleConnectPrompt: "Ligue a sua conta Google",
  ecoImportGoogleConnect: "Ligar Google Fit",
  ecoImportSyncNow: "Sincronizar agora",
  ecoImportGoogleDisconnect: "Desligar Google Fit",
  ecoImportSummary: "Resumo da importação",
  ecoImportStatTotal: "Total",
  ecoImportStatSteps: "Passos",

  labValuePlaceholder: "p. ex. 120",
  labUnitPlaceholder: "p. ex. mg/dL",
  labPerformerPlaceholder: "Dr. Silva / Laboratório ABC",
  labRemove: "Remover resultado",
  labPerformedBy: "por {name}",
  labChartComingSoon: "Gráfico de {name} disponível em breve",
  labChartNoData: "Sem dados para apresentar",
  labChartFilter: "Filtro: {name}",
  labChartDefaultName: "Valor laboratorial",
  labChartRefOptimal: "Ótimo: {value}",
  labChartRefNormal: "Normal: {value}",
  labChartRefHigh: "Alto: {value}",
  labTestLdl: "Colesterol LDL",
  labTestTotalCholesterol: "Colesterol total",
  labTestHdl: "Colesterol HDL",
  labTestTriglycerides: "Triglicerídeos",
  labTestHba1c: "Hemoglobina glicada (HbA1c)",
  labTestHemoglobin: "Hemoglobina",
  labTestWbc: "Leucócitos",
  labTestPlatelets: "Plaquetas",
};

export const healthAllergiesMedsStrings: Record<
  Language,
  HealthAllergiesMedsStrings
> = {
  en,
  de,
  fr,
  es,
  pt,
};
